import React, { useState } from 'react';
import styled from 'styled-components';
import { motion } from 'framer-motion';
import Hero from '../common/Hero';
import ContactForm from '../common/ContactForm';

const ContactContainer = styled.div`
    max-width: 1200px;
    margin: 0 auto;
    padding: 2rem;
`;

const Section = styled.section`
    margin: 4rem 0;
`;

const SectionTitle = styled.h2`
    font-size: 2rem;
    margin-bottom: 2rem;
    color: #333;
    font-family: 'Playfair Display', serif;
    position: relative;

    &:after {
        content: '';
        position: absolute;
        bottom: -10px;
        left: 0;
        width: 60px;
        height: 3px;
        background-color: #6c5ce7;
    }
`;

const Introduction = styled.div`
    margin-bottom: 3rem;

    p {
        font-size: 1.1rem;
        line-height: 1.8;
        color: #555;
    }
`;

const ContactLayout = styled.div`
    display: grid;
    grid-template-columns: 1fr;
    gap: 3rem;

    @media (min-width: 768px) {
        grid-template-columns: 1fr 1fr;
    }
`;

const ContactInfo = styled.div`
    .committee-member {
        margin-bottom: 2rem;

        h3 {
            margin-bottom: 0.5rem;
            color: #333;
        }

        .role {
            color: #6c5ce7;
            font-weight: 500;
            margin-bottom: 1rem;
            display: block;
        }

        .contact-detail {
            display: flex;
            align-items: center;
            margin-bottom: 0.5rem;
            color: #555;

            svg {
                margin-right: 0.5rem;
                color: #6c5ce7;
            }
        }
    }
`;

const StatusMessage = styled.div`
    padding: 1rem;
    border-radius: 8px;
    margin-bottom: 1rem;
    text-align: center;
    
    &.success {
        background-color: #d4edda;
        color: #155724;
        border: 1px solid #c3e6cb;
    }
    
    &.error {
        background-color: #f8d7da;
        color: #721c24;
        border: 1px solid #f5c6cb;
    }
`;

const Contact = () => {
    const [isSubmitting, setIsSubmitting] = useState(false);
    const [submitStatus, setSubmitStatus] = useState(null); // 'success', 'error', or null

    const committeeMembers = [
        {
            id: 1,
            name: 'Pasca Dumani',
            role: 'Clan Committee Chair',
            email: 'info@mlalazi.family',
            phone: '+263 77 570 6709'
        },
        {
            id: 2,
            name: 'Reginah Mlalazi',
            role: 'Events Coordinator',
            email: 'info@mlalazi.family',
            phone: '+263 77 380 5927'
        }
    ];

    const handleFormSubmit = async (formData) => {
        setIsSubmitting(true);
        setSubmitStatus(null);

        try {
            // Build query parameters from form data
            const params = new URLSearchParams({
                phone_number: formData.phone_number || formData.phone || '',
                customer_name: formData.customer_name || formData.name || '',
                email: formData.email || '',
                message: formData.message || ''
            });

            const response = await fetch(`/website/contact?${params.toString()}`, {
                method: 'GET', // Using GET since params are in URL
                headers: {
                    'Content-Type': 'application/json',
                },
            });

            if (response.ok) {
                setSubmitStatus('success');
            } else {
                throw new Error('Failed to submit form');
            }
        } catch (error) {
            console.error('Form submission error:', error);
            setSubmitStatus('error');
        } finally {
            setIsSubmitting(false);
        }
    };

    return (
        <>
            <Hero
                title="Contact Us"
                subtitle="Reach out to the clan committee"
                backgroundImage="/assets/images/logo.jpg"
            />

            <ContactContainer>
                <motion.div
                    initial={{ opacity: 0 }}
                    animate={{ opacity: 1 }}
                    transition={{ duration: 0.5 }}
                >
                    <Introduction>
                        <SectionTitle>Get in Touch</SectionTitle>
                        <p>
                            Have questions about clan events, history, or want to update your information in our registry?
                            The clan committee is here to help. You can reach out to specific committee members directly
                            or use the contact form to send a general inquiry. We look forward to hearing from you!
                        </p>
                    </Introduction>

                    <Section>
                        <ContactLayout>
                            <ContactInfo>
                                <SectionTitle>Clan Committee</SectionTitle>
                                {committeeMembers.map(member => (
                                    <div className="committee-member" key={member.id}>
                                        <h3>{member.name}</h3>
                                        <span className="role">{member.role}</span>
                                        <div className="contact-detail">
                                            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                                                <path d="M20 4H4C2.9 4 2.01 4.9 2.01 6L2 18C2 19.1 2.9 20 4 20H20C21.1 20 22 19.1 22 18V6C22 4.9 21.1 4 20 4ZM20 8L12 13L4 8V6L12 11L20 6V8Z" fill="currentColor"/>
                                            </svg>
                                            {member.email}
                                        </div>
                                        <div className="contact-detail">
                                            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                                                <path d="M6.62 10.79C8.06 13.62 10.38 15.94 13.21 17.38L15.41 15.18C15.69 14.9 16.08 14.82 16.43 14.93C17.55 15.3 18.75 15.5 20 15.5C20.55 15.5 21 15.95 21 16.5V20C21 20.55 20.55 21 20 21C10.61 21 3 13.39 3 4C3 3.45 3.45 3 4 3H7.5C8.05 3 8.5 3.45 8.5 4C8.5 5.25 8.7 6.45 9.07 7.57C9.18 7.92 9.1 8.31 8.82 8.59L6.62 10.79Z" fill="currentColor"/>
                                            </svg>
                                            {member.phone}
                                        </div>
                                    </div>
                                ))}
                            </ContactInfo>

                            <div>
                                <SectionTitle>Send a Message</SectionTitle>

                                {submitStatus === 'success' && (
                                    <StatusMessage className="success">
                                        Thank you! Your message has been sent successfully.
                                    </StatusMessage>
                                )}

                                {submitStatus === 'error' && (
                                    <StatusMessage className="error">
                                        Sorry, there was an error sending your message. Please try again.
                                    </StatusMessage>
                                )}

                                <ContactForm
                                    onSubmit={handleFormSubmit}
                                    isSubmitting={isSubmitting}
                                />
                            </div>
                        </ContactLayout>
                    </Section>
                </motion.div>
            </ContactContainer>
        </>
    );
};

export default Contact;